import {useFreightContext} from 'components/customer/Confirmation/Freight';
import DispatchMethodCard from 'components/customer/Job/components/DispatchMethodCard';
import PickupLocation from 'components/customer/Job/components/PickupLocation';
import {
    DeliveryAddressList,
    DeliveryAddressListHandle,
} from 'components/customer/Job/DeliveryAddressList';
import {JobForm} from 'components/customer/Job/entity/JobForm';
import {invalidateDeliveryAddress} from 'components/customer/Settings/store/settingsApi';
import {ReportJobDetails} from 'components/customer/Confirmation/Freight/ReportJobDetails';
import {RequestedDeliveryDateField} from 'components/customer/Confirmation/Freight/RequestedDeliveryDateField';
import {useAppContext} from 'contexts/ApplicationContext';
import {Formik} from 'formik';
import {
    DispatchMethod as DispatchMethodConstants,
    useJobFormSubmitHandler,
} from 'hooks/Job/Job';
import React, {useCallback, useMemo, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {mobile} from 'shared/Breakpoints';
import {useAppDispatch} from 'store/customer';
import styled from 'styled-components';

export const DispatchMethods = () => {
    const dispatch = useAppDispatch();
    const {jobId} = useParams();
    const {jobFormSubmitHandler} = useJobFormSubmitHandler(
        jobId,
        undefined,
        true
    );
    const {userProfile, places} = useAppContext();
    const showDeliveryDate = !!userProfile?.allowDeliveryDateRequest;

    const listRef = useRef<DeliveryAddressListHandle>(null);
    const {
        selectedDepot,
        formValues,
        setFormValues,
        dispatchMethods,
        dispatchMethod,
        setDispatchMethod,
    } = useFreightContext();

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.currentTarget) {
                const value = Number(event.currentTarget.value);

                if (setDispatchMethod) {
                    setDispatchMethod(value);
                }
            }
        },
        [setDispatchMethod]
    );

    const states = useMemo(() => {
        if (
            places &&
            places.Countries &&
            userProfile &&
            userProfile.countryName
        ) {
            return places.Countries[userProfile.countryName];
        }

        return [];
    }, [userProfile, places]);

    const handleFormSubmit = useCallback(
        async (values: JobForm) => {
            if (setFormValues) {
                setFormValues(values);

                await jobFormSubmitHandler(values);

                dispatch(invalidateDeliveryAddress());
                listRef.current?.reset();
            }
        },
        [setFormValues, jobFormSubmitHandler]
    );

    if (dispatchMethod == undefined || formValues == undefined) return null;

    return (
        <Container>
            <strong>How would you like to receive your order?</strong>

            <div>
                <div>
                    {dispatchMethods
                        ? dispatchMethods.map((method) => {
                              return (
                                  <DispatchMethodCard
                                      key={method.id}
                                      method={method}
                                      values={{
                                          dispatch: dispatchMethod,
                                      }}
                                      handleChange={handleChange}
                                  />
                              );
                          })
                        : null}
                </div>

                <div>
                    {dispatchMethod == DispatchMethodConstants.PICKUP ? (
                        <PickupLocation
                            depotSelected={selectedDepot}
                            userProfile={userProfile}
                            states={states}
                            aside={
                                showDeliveryDate ? (
                                    <RequestedDeliveryDateField />
                                ) : undefined
                            }
                        />
                    ) : null}

                    {dispatchMethod != DispatchMethodConstants.PICKUP ? (
                        <Formik
                            initialValues={formValues}
                            enableReinitialize={true}
                            onSubmit={handleFormSubmit}>
                            <>
                                <ReportJobDetails />
                                {showDeliveryDate ? (
                                    <DeliveryDate>
                                        <RequestedDeliveryDateField />
                                    </DeliveryDate>
                                ) : null}
                                <DeliveryAddressList
                                    ref={listRef}
                                    allowSave={true}
                                    alwaysAllowNewAddress={true}
                                />
                            </>
                        </Formik>
                    ) : null}
                </div>
            </div>
        </Container>
    );
};

const Container = styled.div`
    margin-top: 15px;

    > strong {
        color: ${({theme}) => theme.colors.primary?.main};
        margin-bottom: 10px;
        display: block;
    }

    > div {
        display: flex;
        flex-direction: row;
        gap: 15px;

        ${mobile`
            flex-direction: column;    
        `}

        > div:first-child {
            display: flex;
            flex-direction: row;
            gap: 15px;

            > div {
                padding: 0;
                width: 150px;

                > div {
                    display: none;
                }
            }
        }

        > div:last-child {
            flex: 1;
            min-width: 0;

            > div {
                margin: 0;
            }
        }
    }
`;

// beside the pickup location, the date shows inside its card. for freight it
// goes in a card of its own above the addresses
const DeliveryDate = styled.div`
    max-width: 320px;
    margin-bottom: 15px !important;
`;
